import axios from 'axios';
import config from '@/config.json';

let store = null;
let onUnauthorized = null;

// Wired up in main.js so the client can read the token/selected client from the store and react to 401s
// (token expired / revoked) by logging the user out.
export function setupApi(vuexStore, unauthorizedHandler) {
    store = vuexStore;
    onUnauthorized = unauthorizedHandler;
}

async function call(method, service, data = null, { client = true, loader = true } = {}) {
    if (loader) store.commit('incLoading');

    const headers = {};
    if (store.state.token) {
        headers['Authorization'] = 'Bearer ' + store.state.token;
    }
    if (client && store.state.selectedClientId) {
        headers['X-Client-Id'] = store.state.selectedClientId;
    }

    const options = {
        method,
        url: config.API_BASE + service,
        headers
    };
    if (['post', 'put', 'patch'].includes(method)) {
        options.data = data || {};
    } else {
        options.params = data || {};
    }

    try {
        const response = await axios(options);
        return { success: true, data: response.data, status: response.status };
    } catch (error) {
        const status = error.response ? error.response.status : 0;
        // A 401 while we hold a token means it expired or was revoked: log out. (Login itself carries no
        // token, so its "invalid credentials" 401 never triggers this.)
        if (status === 401 && store.state.token && onUnauthorized) {
            onUnauthorized();
        }
        return { success: false, data: error.response ? error.response.data : null, status };
    } finally {
        if (loader) store.commit('decLoading');
    }
}

// Uploads a single file as multipart/form-data (axios sets the boundary itself). Carries the same auth and
// client scoping headers as call(); used for the member's profile photo.
async function upload(service, file, { client = true, loader = true } = {}) {
    if (loader) store.commit('incLoading');

    const headers = {};
    if (store.state.token) {
        headers['Authorization'] = 'Bearer ' + store.state.token;
    }
    if (client && store.state.selectedClientId) {
        headers['X-Client-Id'] = store.state.selectedClientId;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
        const response = await axios({ method: 'post', url: config.API_BASE + service, headers, data: formData });
        return { success: true, data: response.data, status: response.status };
    } catch (error) {
        const status = error.response ? error.response.status : 0;
        if (status === 401 && store.state.token && onUnauthorized) {
            onUnauthorized();
        }
        return { success: false, data: error.response ? error.response.data : null, status };
    } finally {
        if (loader) store.commit('decLoading');
    }
}

const api = {
    get: (service, data, options) => call('get', service, data, options),
    post: (service, data, options) => call('post', service, data, options),
    upload: (service, file, options) => upload(service, file, options)
};

export default api;
